import * as jose from "jose";
import { useOAuth, useRuntimeConfig } from "#imports";
import type { JwtPayloadWithScopes } from "~/types/oauth";

const alg = "RS256";

export default function () {
  const { public: config } = useRuntimeConfig();

  // Public key is stored as base64 encoded string, so it needs to be decoded in order to use it
  const spki = atob(config.jwtPublicKey);

  const decodeToken = async (token: string): Promise<JwtPayloadWithScopes> => {
    // Hack to prevent cypress test from failing because we use fake tokens in cypress at the moment.
    // When running on localhost or 127.0.0.1 we just decode the JWT token instead of decoding and verifying the signature.
    if (window.location.host.includes("localhost") || window.location.host.includes("127.0.0.1")) {
      return jose.decodeJwt(token);
    }

    const publicKey = await jose.importSPKI(spki, alg);

    try {
      const decodedToken: { payload: JwtPayloadWithScopes } = await jose.jwtVerify(token, publicKey, {
        clockTolerance: 60,
      });

      return decodedToken.payload;
    } catch (error) {
      console.error("JWT signature verification failed", error);
      console.error("Your token for inspection", token);

      const isClockError =
        error instanceof jose.errors.JWTExpired ||
        (error instanceof jose.errors.JWTClaimValidationFailed && error.claim === "nbf");

      if (isClockError) {
        console.error("[JWT] Clock drift detected: JWT time claims failed against client clock", error);
        if (typeof window !== "undefined") {
          localStorage.setItem("auth_error", "clock_drift");
        }
      }

      const { signOut } = await useOAuth();

      await signOut();
    }

    return { scopes: [] };
  };

  return {
    decodeToken,
  };
}
